/**
 * Centralized logging utility for production error tracking.
 *
 * In production, errors should be sent to an external service (Sentry, LogRocket, etc.)
 * For now, this provides a centralized place to:
 * 1. Control log output based on environment
 * 2. Format error messages consistently
 * 3. Add external service integration later
 */

interface LogContext {
  component?: string
  action?: string
  userId?: string
  [key: string]: unknown
}

class Logger {
  private isDev = import.meta.env.DEV

  /**
   * Log an error with optional context.
   * In production, this would send to an external service.
   */
  error(message: string, error?: unknown, context?: LogContext): void {
    const errorDetails = error instanceof Error ? {
      name: error.name,
      message: error.message,
      stack: error.stack,
    } : error

    if (this.isDev) {
      console.error(`[ERROR] ${message}`, errorDetails, context)
    } else {
      // Production: Send to external service
      // TODO: Integrate Sentry/LogRocket when ready
      // For now, still log but with less verbosity
      console.error(`[ERROR] ${message}`)
    }
  }

  /**
   * Log a warning.
   */
  warn(message: string, context?: LogContext): void {
    if (this.isDev) {
      console.warn(`[WARN] ${message}`, context)
    }
  }

  /**
   * Log debug info (dev only).
   */
  debug(message: string, data?: unknown): void {
    if (this.isDev) {
      console.log(`[DEBUG] ${message}`, data)
    }
  }
}

export const logger = new Logger()
export default logger
